import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from '../../../contexts/SnackbarContext';
import { getAdminJobsWithEdgeFunction, getAdminUsersWithEdgeFunction } from '../api/adminWithEdgeFunction';
import { setPocs } from '../../../store/slices/adminSlice';
import { Job, Employee } from '../../../types';

export const useAdminData = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(true);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [users, setUsers] = useState<Employee[]>([]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    const [{ data: jobRows, error: jobsErr }, { data: userRows, error: usersErr }] =
      await Promise.all([getAdminJobsWithEdgeFunction(), getAdminUsersWithEdgeFunction()]);

    if (jobsErr) {
      showSnackbar('Failed to load jobs', 'error');
      setJobs([]);
    } else {
      setJobs(jobRows || []);
    }

    if (usersErr) {
      showSnackbar('Failed to load users', 'error');
      setUsers([]);
      dispatch(setPocs([]));
    } else {
      setUsers(userRows || []);
      dispatch(setPocs(userRows || []));
    }
    setIsLoading(false);
  }, [dispatch, showSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshUsers = async () => {
    setIsLoading(true);
    try {
      const { data: userRows, error: userError } = await getAdminUsersWithEdgeFunction();
      if (userError) {
        showSnackbar('Failed to refresh users', 'error');
      } else {
        setUsers(userRows || []);
        dispatch(setPocs(userRows || []));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const refreshJobs = async () => {
    const { data: jobRows, error: jobsErr } = await getAdminJobsWithEdgeFunction();
    if (jobsErr) {
      showSnackbar('Failed to refresh jobs', 'error');
    } else {
      setJobs(jobRows || []);
    }
  };

  return { jobs, users, isLoading, refreshUsers, refreshJobs, setJobs, fetchData };
};
